import { Injectable } from '@angular/core';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';

@Injectable({ providedIn: 'root' })
export class DeepgramService {
  private deepgramClient: any;
  private connection: any;
  private mediaRecorder?: MediaRecorder;
  private stream?: MediaStream;
  // Track individual streams for better management
  private micStream?: MediaStream;
  private systemAudioStream?: MediaStream;
  private audioContext?: AudioContext;
  private bufferQueue: Array<ArrayBuffer> = [];
  private connectionReady: boolean = false
  private isListening = false;
  private transcriptHandler?: (text: string, isFinal: boolean) => void;
  private pendingStream: Promise<MediaStream> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: any;
  private sessionStart = 0;
  private sessionTimer: any;
  private keepAliveTimer: any;        // sends Deepgram KeepAlive so it doesn't close during silence
  private isReconnecting = false;     // guards against overlapping reconnects
  private readonly maxSessionMs = 3 * 60 * 60 * 1000; // 3 hours
  private readonly maxReconnectDelay = 8000; // ms

  constructor() {
    this.deepgramClient = createClient('23582c2dcc7df088265935322322e3a310cdecc7');
    // Removed pre-request for microphone access. Mic will only be requested on user action.
  }

  startListening(onTranscript: (text: string, isFinal: boolean) => void): Promise<void> {
    // If already listening, stop first to clean up resources
    if (this.isListening) {
      this.stopListening();
    }
    
    // Get merged stream (mic + system audio)
    const streamPromise = this.getMergedStream();

    // Start Deepgram connection initialization in parallel
    const dgPromise = new Promise<void>((resolve, reject) => {
      try {
        this.connection = this.deepgramClient.listen.live({
          model: 'nova-3',
          punctuate: true,
          language: 'en-US',
          interim_results: true,
          streaming: {
            optimize_latency: true,
            latency: 0,
          },
        });

        this.connection.on(LiveTranscriptionEvents.Open, () => {
          this.connectionReady = true;
          this.reconnectAttempts = 0;
          this.startKeepAlive();
          resolve();
        });

        // Auto-reconnect: Deepgram drops the socket after a short idle/server-side window
        // (~10-30s). The active path previously had NO Close handler, so transcription silently
        // died until the mic was toggled. Reconnect instead (reusing the existing recorder/stream).
        this.connection.on(LiveTranscriptionEvents.Close, () => this.scheduleReconnectStart());

        this.connection.on(LiveTranscriptionEvents.Error, (err:any) => {
          console.error('Deepgram connection error:', err);
          if (this.connectionReady) {
            // Already running — treat as a drop and reconnect rather than failing.
            this.scheduleReconnectStart();
          } else {
            reject(err);
          }
        });

        this.connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
          const transcript = data.channel?.alternatives?.[0]?.transcript;
          const isFinal = data.is_final ?? false;
          if (transcript && this.transcriptHandler) {
            this.transcriptHandler(transcript, isFinal);
          }
        });
      } catch (err) {
        reject(err);
      }
    });

    this.isListening = true;
    this.transcriptHandler = onTranscript;
    this.sessionStart = Date.now();
    
    try { clearTimeout(this.sessionTimer); } catch (e) { }
    this.sessionTimer = setTimeout(() => {
      console.log('Deepgram session reached max duration, stopping');
      this.stopListening();
    }, this.maxSessionMs);

    this.reconnectAttempts = 0;
    try { clearTimeout(this.reconnectTimer); } catch (e) { }

    // Start both merged stream and Deepgram initialization in parallel, then set up MediaRecorder
    return Promise.all([streamPromise, dgPromise])
      .then(([mergedStream]) => {
        this.stream = mergedStream;
        this.mediaRecorder = new MediaRecorder(mergedStream, {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000
        });

        this.mediaRecorder.addEventListener('dataavailable', (event) => {
          if (event.data.size > 0) {
            event.data.arrayBuffer().then(buffer => {
              if (this.connectionReady && this.connection) {
                try {
                  this.connection.send(buffer);
                } catch (e) {
                  this.bufferQueue.push(buffer);
                }
              } else {
                this.bufferQueue.push(buffer);
              }
            });
          }
        });

        this.mediaRecorder.start(2);
      })
      .catch((err) => {
        console.error('Failed to start listening:', err);
        this.isListening = false;
        throw err;
      });
  }

  // Keep the Deepgram socket alive during quiet stretches. Deepgram closes idle connections,
  // which was the ~30s cutoff; a periodic KeepAlive control message prevents that.
  private startKeepAlive(): void {
    try { clearInterval(this.keepAliveTimer); } catch (e) { }
    this.keepAliveTimer = setInterval(() => {
      try {
        if (this.isListening && this.connection && this.connectionReady &&
            typeof this.connection.keepAlive === 'function') {
          this.connection.keepAlive();
        }
      } catch (e) { /* ignore */ }
    }, 6000);
  }

  // Recreate the Deepgram connection after a drop, REUSING the existing MediaRecorder/stream
  // (the dataavailable handler always sends to the current this.connection). Backoff + guarded
  // so it never fights itself or fires after the user stops.
  private scheduleReconnectStart(): void {
    this.connectionReady = false;
    try { clearInterval(this.keepAliveTimer); } catch (e) { }
    if (!this.isListening || this.isReconnecting) return;

    const elapsed = Date.now() - this.sessionStart;
    if (elapsed >= this.maxSessionMs) {
      this.stopListening();
      return;
    }

    this.isReconnecting = true;
    this.reconnectAttempts = (this.reconnectAttempts || 0) + 1;
    const base = 300;
    const delay = Math.min(base * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
    console.log(`[Deepgram] connection dropped - reconnecting (attempt ${this.reconnectAttempts}) in ${delay}ms`);

    try { clearTimeout(this.reconnectTimer); } catch (e) { }
    this.reconnectTimer = setTimeout(() => {
      this.isReconnecting = false;
      if (!this.isListening) return;
      this.initLiveTranscriptionConnectionOnly()
        .then(() => { this.reconnectAttempts = 0; this.startKeepAlive(); })
        .catch(() => this.scheduleReconnectStart());
    }, delay);
  }

  private initLiveTranscription(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        this.bufferQueue = [];
        this.connectionReady = false;
        
        // Start Deepgram connection immediately
        this.connection = this.deepgramClient.listen.live({
          model: 'nova-3',
          punctuate: true,
          language: 'en-US',
          interim_results: true,
          streaming: {
            optimize_latency: true,
            latency: 0,
          },
        });

        // Get microphone access in parallel
        const streamPromise = this.pendingStream || navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 48000,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });

        // Set up Deepgram handlers
        this.connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
          const transcript = data.channel?.alternatives?.[0]?.transcript;
          const isFinal = data.is_final ?? false;
          if (transcript && this.transcriptHandler) {
            this.transcriptHandler(transcript, isFinal);
          }
        });

        // Wait for both mic and connection, but start them in parallel
        const stream = await streamPromise;
        this.stream = stream;

        // Initialize MediaRecorder with optimized settings
        this.mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000
        });

        // Process audio chunks immediately without blocking
        this.mediaRecorder.addEventListener('dataavailable', (event) => {
          if (event.data.size > 0) {
            event.data.arrayBuffer().then(buffer => {
              if (this.connectionReady && this.connection) {
                try {
                  this.connection.send(buffer);
                } catch (e) {
                  this.bufferQueue.push(buffer);
                }
              } else {
                this.bufferQueue.push(buffer);
              }
            });
          }
        });

        // Start recording immediately
        try {
          this.mediaRecorder.start(2);
        } catch (e) {
          console.warn('MediaRecorder start may not be supported or failed:', e);
        }

        // When connection opens, flush queued buffers
        this.connection.on(LiveTranscriptionEvents.Open, () => {
          this.connectionReady = true;
          // reset reconnect attempts on successful open
          this.reconnectAttempts = 0;
          try {
            while (this.bufferQueue.length > 0 && this.connection) {
              const b = this.bufferQueue.shift()!;
              try { this.connection.send(b); } catch (e) { /* ignore send errors */ }
            }
          } catch (e) { /* ignore */ }
          resolve();
        });

        // On close or error, attempt reconnect (unless user stopped or session expired)
        const scheduleReconnect = (err?: any) => {
          if (err) console.warn('Deepgram connection closed/errored:', err);
          if (!this.isListening) {
            // user stopped
            return;
          }

          const elapsed = Date.now() - this.sessionStart;
          if (elapsed >= this.maxSessionMs) {
            console.log('Session expired, will not reconnect');
            this.stopListening();
            return;
          }

          this.reconnectAttempts = (this.reconnectAttempts || 0) + 1;
          // exponential backoff with jitter
          const base = 500;
          const delay = Math.min(base * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
          const jitter = delay * (0.5 + Math.random() * 0.5);
          console.log(`Scheduling Deepgram reconnect attempt ${this.reconnectAttempts} in ${Math.round(jitter)}ms`);
          try { clearTimeout(this.reconnectTimer); } catch (e) { }
          this.reconnectTimer = setTimeout(() => {
            if (!this.isListening) return;
            const timeLeft = this.maxSessionMs - (Date.now() - this.sessionStart);
            if (timeLeft <= 0) {
              console.log('No time left in session for reconnect');
              this.stopListening();
              return;
            }
            // Try to recreate the connection and rewire handlers; keep the existing mediaRecorder/stream running
            try {
              // create a fresh connection and attach listeners by calling initLiveTranscriptionConnectionOnly
              this.initLiveTranscriptionConnectionOnly().catch((e) => {
                console.warn('Reconnect attempt failed:', e);
                scheduleReconnect(e);
              });
            } catch (e) {
              console.warn('Reconnect scheduling error:', e);
              scheduleReconnect(e);
            }
          }, jitter);
        };

        this.connection.on(LiveTranscriptionEvents.Close, () => scheduleReconnect());
        this.connection.on(LiveTranscriptionEvents.Error, (err: any) => scheduleReconnect(err));
      } catch (err) {
        console.error('Could not get user media:', err);
        this.isListening = false;
        reject(err);
      }
    });
  }

  // Create only the Deepgram connection and wire handlers without re-requesting getUserMedia
  private initLiveTranscriptionConnectionOnly(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (this.connection) {
          try { this.connection.close(); } catch (e) { }
          try { this.connection.removeAllListeners?.(); } catch (e) { }
          this.connection = undefined;
        }

        this.connection = this.deepgramClient.listen.live({
          model: 'nova-3',
          punctuate: true,
          language: 'en-US',
          interim_results: true,
          streaming: { optimize_latency: true, latency: 0 },
        });

        this.connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
          const transcript = data.channel?.alternatives?.[0]?.transcript;
          const isFinal = data.is_final ?? false;
          if (transcript && this.transcriptHandler) {
            this.transcriptHandler(transcript, isFinal);
          }
        });

        this.connection.on(LiveTranscriptionEvents.Open, () => {
          this.connectionReady = true;
          this.reconnectAttempts = 0;
          this.startKeepAlive();
          try {
            while (this.bufferQueue.length > 0 && this.connection) {
              const b = this.bufferQueue.shift()!;
              try { this.connection.send(b); } catch (e) { /* ignore send errors */ }
            }
          } catch (e) { /* ignore */ }
          resolve();
        });

        this.connection.on(LiveTranscriptionEvents.Close, () => {
          // Keep the reconnect loop self-sustaining: if this reconnected socket also drops,
          // schedule another reconnect (guarded so it won't fire after the user stops).
          try { this.connectionReady = false; } catch (e) { }
          this.scheduleReconnectStart();
        });

        this.connection.on(LiveTranscriptionEvents.Error, (err: any) => {
          console.warn('Deepgram connection error (connection-only):', err);
          this.connectionReady = false;
          reject(err);
        });

        // If we already have a mediaRecorder capturing, continue sending queued or incoming chunks.
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Get merged stream of microphone and system audio
   * Tracks both streams separately for proper cleanup
   */
  private async getMergedStream(): Promise<MediaStream> {
    try {
      // Close previous audio context if it exists
      if (this.audioContext && this.audioContext.state !== 'closed') {
        try {
          await this.audioContext.close();
        } catch (err) {
          console.warn('Error closing previous audio context:', err);
        }
      }

      // Create new audio context
      const audioCtx = new AudioContext();
      this.audioContext = audioCtx;

      // 1. Get microphone stream
      console.log('📍 Requesting microphone access...');
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      console.log('✅ Microphone stream acquired');

      // 2. Try to get system audio stream (Stereo Mix / Loopback)
      let systemAudioAvailable = false;
      try {
        console.log('📍 Searching for system audio device...');
        const devices = await navigator.mediaDevices.enumerateDevices();
        const systemDevice = devices.find(d =>
          d.kind === 'audioinput' &&
          /stereo\s*mix|what.*hear|loopback|virtual|cable/i.test(d.label.toLowerCase())
        );

        if (systemDevice) {
          console.log(`✅ Found system audio device: ${systemDevice.label}`);
          this.systemAudioStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: systemDevice.deviceId },
              channelCount: 1,
              sampleRate: 48000
            }
          });
          systemAudioAvailable = true;
          console.log('✅ System audio stream acquired');
        } else {
          console.log('⚠️  No system audio device found (Stereo Mix not enabled)');
        }
      } catch (err) {
        console.warn('⚠️  System audio unavailable (this is normal):', err);
      }

      // 3. Create merged stream destination
      const dest = audioCtx.createMediaStreamDestination();

      // Add microphone source
      const micSource = audioCtx.createMediaStreamSource(this.micStream);
      micSource.connect(dest);
      console.log('🎤 Connected microphone source');

      // Add system audio source if available
      if (this.systemAudioStream && systemAudioAvailable) {
        const sysSource = audioCtx.createMediaStreamSource(this.systemAudioStream);
        sysSource.connect(dest);
        console.log('🖥️  Connected system audio source');
        console.log('✨ Both microphone and system audio will be processed simultaneously');
      }

      return dest.stream;
    } catch (err) {
      console.error('Error creating merged stream:', err);
      throw err;
    }
  }

  stopListening() {
    console.log('Stopping Deepgram listening...');
    
    // Set flags first to prevent any new audio processing
    this.isListening = false;
    this.connectionReady = false;

    // Clear all timers
    try { clearTimeout(this.sessionTimer); } catch (e) { }
    try { clearTimeout(this.reconnectTimer); } catch (e) { }
    try { clearInterval(this.keepAliveTimer); } catch (e) { }
    this.isReconnecting = false;
    
    // Stop and clean up MediaRecorder
    if (this.mediaRecorder) {
      try {
        if (this.mediaRecorder.state !== 'inactive') {
          this.mediaRecorder.stop();
        }
      } catch (e) {
        console.warn('MediaRecorder stop error:', e);
      }
      
      try {
        // Clean up event handlers
        this.mediaRecorder.ondataavailable = null;
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.onerror = null;
      } catch (e) { 
        console.warn('MediaRecorder cleanup error:', e);
      }
      
      this.mediaRecorder = undefined;
    }

    // Close and clean up Deepgram connection
    if (this.connection) {
      try {
        this.connection.close();
      } catch (e) {
        console.warn('Deepgram close error:', e);
      }
      
      try {
        if (typeof this.connection.removeAllListeners === 'function') {
          this.connection.removeAllListeners();
        }
      } catch (e) {
        console.warn('Deepgram listener cleanup error:', e);
      }
      
      this.connection = undefined;
    }

    // Stop all media tracks
    if (this.stream) {
      try {
        const tracks = this.stream.getTracks();
        tracks.forEach(track => {
          try {
            track.stop();
            this.stream?.removeTrack(track);
          } catch (e) {
            console.warn('Track stop error:', e);
          }
        });
      } catch (e) {
        console.warn('Stream cleanup error:', e);
      }
      this.stream = undefined;
    }

    // Stop microphone stream specifically
    if (this.micStream) {
      try {
        const tracks = this.micStream.getTracks();
        tracks.forEach(track => {
          try {
            track.stop();
            console.log('🎤 Microphone track stopped');
          } catch (e) {
            console.warn('Microphone track stop error:', e);
          }
        });
      } catch (e) {
        console.warn('Microphone stream cleanup error:', e);
      }
      this.micStream = undefined;
    }

    // Stop system audio stream specifically
    if (this.systemAudioStream) {
      try {
        const tracks = this.systemAudioStream.getTracks();
        tracks.forEach(track => {
          try {
            track.stop();
            console.log('🖥️  System audio track stopped');
          } catch (e) {
            console.warn('System audio track stop error:', e);
          }
        });
      } catch (e) {
        console.warn('System audio stream cleanup error:', e);
      }
      this.systemAudioStream = undefined;
    }

    // Close audio context to fully release resources
    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        this.audioContext.close().then(() => {
          console.log('🔌 AudioContext closed successfully');
        }).catch((err) => {
          console.warn('AudioContext close error:', err);
        });
      } catch (err) {
        console.warn('AudioContext close error:', err);
      }
      this.audioContext = undefined;
    }

    // Reset all state
    this.bufferQueue = [];
    this.transcriptHandler = undefined;
    this.reconnectAttempts = 0;
    this.pendingStream = null;
    
    console.log('Deepgram listening stopped, all resources cleaned up');
  }
}
